// The identity switch of the shaders (ui-map-interface.md §2.5, R3293).
//
// The dark identity adds a glow as light; added light disappears on
// paper. Each world pass therefore gets two compile-time constants, in
// GLSL and in WGSL, from the identity the page picked at load:
//
//   AP_LIGHT   1 in the light identity, 0 in the dark one;
//   AP_GLOW_K  the factor on the alpha of a glow: `glow_alpha_k` of
//              data/presentation/light.toml in the light identity, 1 in
//              the dark one.
//
// The shared preludes carry them (`PRELUDE_BODY`, `PRELUDE_BODY_WGSL`).
// The depth and the territory programs take `AP_GLOW_K` (R3295), the
// three instanced batches take both constants through `addglow.ts`
// (R3297), and the ambience, the wake and the depth layers read
// `AP_GLOW_K` on the CPU (R3294, R3295). The values are constants of the source, so the compiler
// folds a branch on them away and the frame pays nothing.

import { LIGHT_RULE } from '../../gen/inks';
import { LIGHT } from '../../identity/pick';

/** 1 on a page in the light identity, else 0. */
export const AP_LIGHT: number = LIGHT ? 1 : 0;

/** The factor on the alpha of a glow: the rule's figure on paper, 1 in the dark. */
export const AP_GLOW_K: number = LIGHT ? LIGHT_RULE.glowAlphaK : 1;

/** A float literal both shading languages take: a whole number keeps `.0`. */
function lit(v: number): string {
  return Number.isInteger(v) ? v.toFixed(1) : String(v);
}

/** The two constants as GLSL ES 3.00 lines. */
export const IDENTITY_GLSL = `const float AP_LIGHT = ${lit(AP_LIGHT)};
const float AP_GLOW_K = ${lit(AP_GLOW_K)};
`;

/** The two constants as WGSL lines. */
export const IDENTITY_WGSL = `const AP_LIGHT: f32 = ${lit(AP_LIGHT)};
const AP_GLOW_K: f32 = ${lit(AP_GLOW_K)};
`;
